import Constants from './constants';
import moment from 'moment';
import { isEmpty } from './generic_utils';

const PRODUCT_TYPE_PACE = [Constants.PRODUCT_TYPE_PACE, Constants.PRODUCT_TYPE_EE_PACE];
const { PRODUCT_TYPES_TITLE_DOES_NOT_APPLY } = Constants;

export default class EligibilityHelper {
  static hasEligibilityBeenSubmitted = (eligibility) => !(!eligibility || isEmpty(eligibility));

  static hasCreditCheck = (eligibility) => eligibility && eligibility.creditCheck;

  static isInPersonCredit = (eligibility) =>
    eligibility && eligibility.creditCheck && eligibility.creditCheck.type === Constants.CREDIT_TYPE_IN_PERSON;

  static isEmailCredit = (eligibility) =>
    eligibility && eligibility.creditCheck && eligibility.creditCheck.type === Constants.CREDIT_TYPE_EMAIL;

  static eligibilityType = (eligibility) => eligibility && eligibility.creditCheck && eligibility.creditCheck.type;

  static hasCreditAndTitlePassed = (eligibility) => eligibility && eligibility.overallStatus === 'approved';

  static hasStatusPassed = (status) => ['Pass'].includes(status);

  static hasStatusFailed = (status) => ['Cancelled', 'Fail', 'Expired'].includes(status);

  static hasStatusUnknown = (status) => ['NoResponse'].includes(status);

  static hasStatusPended = (status) => ['Pended'].includes(status);

  static hasCreditPassed = (eligibility) =>
    this.hasCreditCheck(eligibility) &&
    eligibility.creditCheck.status &&
    this.hasStatusPassed(eligibility.creditCheck.status);

  static hasCreditFailed = (eligibility) =>
    this.hasCreditCheck(eligibility) &&
    eligibility.creditCheck.status &&
    this.hasStatusFailed(eligibility.creditCheck.status);

  hasCreditPended = (eligibility) =>
    this.hasCreditCheck(eligibility) &&
    eligibility.creditCheck.status &&
    this.hasStatusPended(eligibility.creditCheck.status);

  static hasCreditStips(eligibility) {
    if (!eligibility) {
      return [];
    }
    if (!eligibility.creditCheck.financingProducts) {
      return [];
    }
    const allStips = eligibility.creditCheck.financingProducts[0].rateCards.map(function (rateCard) {
      return rateCard.stips;
    });
    const reducedStips = allStips.reduce((returnStips, stipsForSingleRateCard) => {
      return returnStips.concat(stipsForSingleRateCard);
    }, []);

    return reducedStips.length >= 1;
  }

  static hasPropertyRuleFailed(eligibility) {
    const { propertyRules } = eligibility;

    if (propertyRules && propertyRules.length > 0) {
      for (let i = 0; i < propertyRules.length; i++) {
        if (propertyRules[i].status === Constants.PROPERTY_RULE_FAIL) {
          return true;
        }
      }
    }
    return false;
  }

  static hasTitleFailed = (eligibility) =>
    eligibility.title && [Constants.TITLE_STATUS_REJECT].includes(eligibility.title.status);

  static hasTitleInitiated = (eligibility) =>
    eligibility.title && [Constants.TITLE_STATUS_INITIATED].includes(eligibility.title.status);

  static doesTitleApply(eligibility) {
    if (eligibility !== null) {
      const {
        creditCheck: { financingProducts: productCreditChecks },
      } = eligibility;
      return (
        productCreditChecks.length > 1 ||
        typeof productCreditChecks.find(
          ({ financingProductType }) => PRODUCT_TYPES_TITLE_DOES_NOT_APPLY.indexOf(financingProductType) !== -1
        ) === 'undefined'
      );
    }
    return true;
  }

  static doesTitleNeedReview = (eligibility) =>
    eligibility.title && [Constants.TITLE_STATUS_NEEDS_REVIEW].includes(eligibility.title.status);

  static hasConditionallyApproved = (eligibility) =>
    eligibility.title && [Constants.TITLE_STATUS_CONDITIONAL_APPROVE].includes(eligibility.title.status);

  static hasTitlePassed = (eligibility) =>
    eligibility.title && [Constants.TITLE_STATUS_FAST_APPROVE].includes(eligibility.title.status);

  static getOverallStatus(eligibility, productType) {
    const {
      creditCheck: { financingProducts },
    } = eligibility;
    const financingProduct = financingProducts.filter((product) => {
      return product.financingProductType === productType;
    });
    return (financingProduct && financingProduct.length && financingProduct[0].overallStatus) || '';
  }

  static getPropertyText(productType, eligibility) {
    const { propertyRules } = eligibility;
    const propertyFailed = this.hasPropertyRuleFailed(eligibility);
    let propertyText = null;

    if (PRODUCT_TYPE_PACE.indexOf(productType) !== -1) {
      if (propertyFailed) {
        propertyText = 'Property Ineligible';
      } else if (propertyRules && propertyRules.length) {
        propertyText = 'Property Approved.';
      } else {
        propertyText = 'Unable to Retrieve Property Information';
      }
    }

    return propertyText;
  }

  static getPrimaryCcProduct = (eligibility) => {
    const primaryCcxfpId = eligibility?.creditCheck?.primaryCcxfpId;
    return eligibility?.creditCheck?.financingProducts?.find(({ id }) => id === primaryCcxfpId);
  };

  static validateDate(value, allowBlanks = false) {
    if (value === '') {
      return allowBlanks ? '' : 'Date of birth cannot be blank';
    }
    const birthDate = moment(value, 'MM/DD/YYYY', true);
    if (!birthDate.isValid()) {
      return 'Date of birth is invalid';
    }
    const minAgeDate = moment().subtract(18, 'years').startOf('date');
    const maxAgeDate = moment('1900-01-01');
    if (birthDate.isAfter(minAgeDate)) {
      return 'Applicant must be at least 18 years of age';
    } else if (birthDate.isBefore(maxAgeDate)) {
      return 'Date of birth must be after 01/01/1900';
    }
    return '';
  }

  static validateIncome(rawValue, allowBlanks = false) {
    if (rawValue === '') {
      return allowBlanks ? '' : 'Annual Income cannot be blank';
    }
    const value = parseInt(rawValue);

    if (isNaN(value)) {
      return 'Annual Income must be a number';
    } else if (value > 999999999) {
      return 'Annual Income must be no more than 10 digits';
    } else if (value < 0) {
      return 'Annual Income must be positive';
    }
    return '';
  }

  // Currently this is only used for Quickcheck where SSN is only last 4 digits or can be left blank
  static validateSSN(value, length = 4, allowBlanks = false) {
    if (value.length === 0) {
      return allowBlanks ? '' : 'SSN cannot be blank';
    }
    // Checks value only contains digits or is an empty string
    if (!/^\d*$/.test(value)) {
      return 'SSN can only contain numbers';
    } else if (value.length !== length) {
      return `SSN must be ${length} digits`;
    }
    return '';
  }

  static valueOrNull(value) {
    if (value == null) {
      return value;
    }
    value = value.trim();
    return value || null;
  }
}
